import { Component, inject, ChangeDetectionStrategy, computed, signal } from '@angular/core';

import { Store } from '@ngrx/store';

import { BookingAppState } from '@fcom/common/interfaces/booking';
import { BoundType } from '@fcom/dapi/interfaces';
import { ModalButtons, TabLayoutType } from '@fcom/ui-components';
import { BookingService } from '@fcom/booking/services';
import { FinnairBoundItem } from '@fcom/dapi/api/models';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces';
import { GtmService } from '@fcom/common/gtm';
import { BOOKING_STEPS } from '@fcom/common/config/booking-config';
import { cartDataSelector } from '@fcom/booking/store/selectors/cart.selector';
import { TripType } from '@fcom/core/interfaces';
import { ExpanderStatus } from '@fcom/booking-widget/interfaces';
import { FinnairThemeService } from '@fcom/common/services';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { getTripTypeFromBounds } from '../../../utils/common-booking.utils';

interface TicketSummaryModalState {
  open: boolean;
  bound: FinnairBoundItem;
  boundType: BoundType;
}

interface BoundSummary {
  bound: FinnairBoundItem;
  departureCity: string;
  arrivalCity: string;
  boundType: BoundType;
}

@Component({
  selector: 'fin-pax-flight-review-summary',
  templateUrl: './pax-flight-review-summary.component.html',
  styleUrls: ['./pax-flight-review-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PaxFlightReviewSummaryComponent {
  private bookingService = inject(BookingService);
  private store$ = inject<Store<BookingAppState>>(Store);
  private gtmService = inject(GtmService);
  private themeService = inject(FinnairThemeService);

  readonly isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  ModalButtons = ModalButtons;
  TabLayoutType = TabLayoutType;

  readonly currentOffer = this.store$.selectSignal(cartDataSelector);

  readonly ticketSummaryModal = signal<TicketSummaryModalState>({
    open: false,
    bound: null,
    boundType: null,
  });

  readonly boundSummary = computed<BoundSummary[]>(() => {
    const cart = this.currentOffer();

    if (!cart) {
      return [];
    }

    const tripType = getTripTypeFromBounds(cart.bounds);

    return cart.bounds.map((bound, i) => {
      const departureCity = cart.locations[bound.departure.locationCode]?.cityName;
      const arrivalCity = cart.locations[bound.arrival.locationCode]?.cityName;

      return {
        bound,
        departureCity: departureCity || bound.departure.locationCode,
        arrivalCity: arrivalCity || bound.arrival.locationCode,
        boundType: this.getBoundTypeFromBound(tripType, i),
      };
    });
  });

  getBoundTypeFromBound(tripType: TripType, index: number): BoundType {
    if (tripType === TripType.ONEWAY) {
      return BoundType.outbound;
    }

    if (tripType === TripType.RETURN) {
      return index === 0 ? BoundType.outbound : BoundType.inbound;
    }

    return BoundType.multi;
  }

  showTicketSummaryModal(boundSummary: BoundSummary): void {
    this.ticketSummaryModal.set({
      open: true,
      bound: boundSummary.bound,
      boundType: boundSummary.boundType,
    });

    this.gtmService.trackElement(
      'pax-details-flight-review::view-flight-details',
      GaContext.PAX_DETAILS_FLIGHT_REVIEW,
      ElementTypes.LINK,
      undefined,
      ElementActions.CLICK
    );
  }

  closeModals(): void {
    this.ticketSummaryModal.set({
      open: false,
      bound: null,
      boundType: null,
    });
  }

  returnToFlightSelection(): void {
    this.bookingService.setStep({ step: BOOKING_STEPS.FLIGHT_SELECTION });
    this.bookingService.setGlobalBookingWidgetExpanderStatus(ExpanderStatus.OPEN);
  }
}
